
import { Injectable, signal, computed, inject } from '@angular/core';
import { Auth, signInWithEmailAndPassword, User as FirebaseUser, onAuthStateChanged, signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from '@angular/fire/auth';
import { Firestore, doc, getDoc, updateDoc, setDoc, serverTimestamp } from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Router } from '@angular/router';
import { User, UserRole } from '../models/interfaces';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private currentUserSignal = signal<User | null>(null);
    private loadingSignal = signal<boolean>(false);
    private errorSignal = signal<string | null>(null);

    currentUser = computed(() => this.currentUserSignal());
    isAuthenticated = computed(() => this.currentUserSignal() !== null);
    isLoading = computed(() => this.loadingSignal());
    error = computed(() => this.errorSignal());

    /**
     * Normalized role signal — always lowercase, with value one of:
     * 'superadmin' | 'admin' | 'user'
     * Handles legacy Firestore values: 'User', 'Admin', 'SuperAdmin', 'super-admin'
     */
    role = computed<UserRole | null>(() => {
        const raw = this.currentUserSignal()?.role as string | undefined;
        return raw ? AuthService.normalizeRole(raw) : null;
    });

    /** Alias kept for backward compatibility */
    userRole = this.role;

    /** true for superadmin AND admin */
    isAdminOrAbove = computed(() => {
        const r = this.role();
        return r === 'superadmin' || r === 'admin';
    });
    /** true only for the plain 'user' role */
    isUserOnly = computed(() => this.role() === 'user');
    /** true only for superadmin */
    isSuperAdmin = computed(() => this.role() === 'superadmin');

    /** Normalize any legacy/capitalized role string to a canonical UserRole */
    static normalizeRole(raw: string): UserRole {
        const r = raw.toLowerCase().replace(/[-_\s]/g, '');
        if (r === 'superadmin') return 'superadmin';
        if (r === 'admin') return 'admin';
        return 'user';
    }

    // Resolves once Firebase Auth has emitted its first state (ready to guard routes)
    readonly authReady: Promise<void>;
    private resolveAuthReady!: () => void;

    private functions = inject(Functions);

    constructor(
        private auth: Auth,
        private firestore: Firestore,
        private storage: Storage,
        private router: Router
    ) {
        this.authReady = new Promise(resolve => { this.resolveAuthReady = resolve; });
        this.initAuthStateListener();
    }

    private initAuthStateListener(): void {
        // Restore user from localStorage immediately (for faster UI updates)
        const cachedUser = localStorage.getItem('currentUser');
        if (cachedUser) {
            try {
                const parsed = JSON.parse(cachedUser);
                // Normalize role so cached legacy values still work
                if (parsed?.role) parsed.role = AuthService.normalizeRole(parsed.role);
                this.currentUserSignal.set(parsed);
            } catch (e) {
                console.error('Failed to parse cached user:', e);
                localStorage.removeItem('currentUser');
            }
        }
        // If cached user exists, resolve authReady immediately so guard doesn't wait
        if (this.currentUserSignal()) {
            this.resolveAuthReady();
        }

        // Listen to Firebase auth state changes
        onAuthStateChanged(this.auth, async (firebaseUser: FirebaseUser | null) => {
            if (firebaseUser) {
                // Fetch full user profile from Firestore (auto-creates if first login)
                const userData = await this.getUserProfile(firebaseUser.uid, firebaseUser);
                if (userData) {
                    this.currentUserSignal.set(userData);
                    localStorage.setItem('currentUser', JSON.stringify(userData));
                }
            } else {
                this.currentUserSignal.set(null);
                localStorage.removeItem('currentUser');
            }
            // Signal that auth is ready (first emission resolved)
            this.resolveAuthReady();
        });
    }

    async login(email: string, password: string): Promise<void> {
        this.loadingSignal.set(true);
        this.errorSignal.set(null);

        try {
            const credential = await signInWithEmailAndPassword(this.auth, email.trim(), password);
            const userData = await this.getUserProfile(credential.user.uid, credential.user);

            if (userData) {
                this.currentUserSignal.set(userData);
                localStorage.setItem('currentUser', JSON.stringify(userData));
                this.router.navigate(['/home']);
            } else {
                throw new Error('Unable to load user profile. Please try again or contact your administrator.');
            }
        } catch (error: any) {
            console.error('Login Error:', error);
            let msg = 'Invalid email or password. Please try again.';
            if (error.code === 'auth/invalid-email') {
                msg = 'Please enter a valid email address.';
            } else if (error.code === 'auth/too-many-requests') {
                msg = 'Too many failed attempts. Please try again later or reset your password.';
            } else if (error.code === 'auth/network-request-failed') {
                msg = 'Network error. Check your internet connection and try again.';
            }
            this.errorSignal.set(msg);
        } finally {
            this.loadingSignal.set(false);
        }
    }

    async sendPasswordReset(email: string): Promise<void> {
        this.loadingSignal.set(true);
        this.errorSignal.set(null);
        try {
            const fn = httpsCallable<{ email: string }, { success: boolean }>(
                this.functions,
                'sendPasswordResetEmail'
            );
            await fn({ email: email.trim() });
        } catch (error: any) {
            console.error('sendPasswordReset error:', error);
            let msg = 'Failed to send reset email. Please try again.';
            if (error?.code === 'functions/invalid-argument') {
                msg = 'Please enter a valid email address.';
            } else if (error?.code === 'functions/resource-exhausted') {
                msg = 'Too many requests. Please try again later.';
            }
            this.errorSignal.set(msg);
            throw error;
        } finally {
            this.loadingSignal.set(false);
        }
    }

    async logout(): Promise<void> {
        try {
            await signOut(this.auth);
            this.currentUserSignal.set(null);
            localStorage.removeItem('currentUser');
            this.router.navigate(['/login']);
        } catch (error) {
            console.error('Logout error:', error);
        }
    }

    async getUserProfile(userId: string, firebaseUser?: FirebaseUser): Promise<User | null> {
        try {
            const userRef = doc(this.firestore, 'users', userId);
            const userDoc = await getDoc(userRef);

            if (!userDoc.exists()) {
                // First login — try to auto-create Firestore profile with default role 'user'
                const email = firebaseUser?.email || '';
                const name = firebaseUser?.displayName || email.split('@')[0] || 'User';
                const newProfile = {
                    name,
                    email,
                    employeeId: userId.slice(0, 8).toUpperCase(),
                    department: 'General',
                    role: 'user',
                    permissions: ['read'],
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };

                try {
                    await setDoc(userRef, newProfile);
                    console.log('Auto-created Firestore profile for new user:', email);
                } catch (writeErr) {
                    console.warn('Could not auto-create Firestore profile (rules may block writes). Using in-memory profile.', writeErr);
                }

                return {
                    id: userId,
                    ...newProfile,
                    role: 'user',
                    createdAt: new Date(),
                    updatedAt: new Date()
                } as User;
            }

            const data = userDoc.data();
            // Normalize role so legacy values ('User', 'Admin', 'SuperAdmin') work
            const rawRole = (data['role'] as string) || 'user';
            return {
                id: userDoc.id,
                ...data,
                role: AuthService.normalizeRole(rawRole),
                createdAt: data['createdAt']?.toDate() || new Date(),
                updatedAt: data['updatedAt']?.toDate() || new Date()
            } as User;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            // Fallback: return a minimal profile from Firebase Auth data
            if (firebaseUser) {
                const email = firebaseUser.email || '';
                const name = firebaseUser.displayName || email.split('@')[0] || 'User';
                return {
                    id: userId,
                    name,
                    email,
                    employeeId: userId.slice(0, 8).toUpperCase(),
                    department: 'General',
                    role: 'user',
                    permissions: ['read'],
                    createdAt: new Date(),
                    updatedAt: new Date()
                } as User;
            }
            return null;
        }
    }

    hasPermission(permission: string): boolean {
        const user = this.currentUserSignal();
        return user ? user.permissions.includes(permission) : false;
    }

    async updateUserProfile(updates: Partial<User>): Promise<void> {
        const user = this.currentUserSignal();
        if (!user) throw new Error('No user logged in');

        try {
            // Update Firestore
            await updateDoc(doc(this.firestore, 'users', user.id), {
                ...updates,
                updatedAt: new Date()
            });

            // Update local state
            const updatedUser = { ...user, ...updates };
            this.currentUserSignal.set(updatedUser);
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    }

    async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
        const firebaseUser = this.auth.currentUser;
        if (!firebaseUser || !firebaseUser.email) throw new Error('No user logged in');

        try {
            // Reauthenticate the user
            const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
            await reauthenticateWithCredential(firebaseUser, credential);

            // Update password
            await updatePassword(firebaseUser, newPassword);
        } catch (error: any) {
            console.error('Error updating password:', error);
            if (error.code === 'auth/wrong-password') {
                throw new Error('Current password is incorrect');
            }
            throw error;
        }
    }

    async updateUserPhoto(photoFile: File): Promise<void> {
        const user = this.currentUserSignal();
        if (!user) throw new Error('No user logged in');

        try {
            // Upload to Firebase Storage
            const storageRef = ref(this.storage, `user-photos/${user.id}/${Date.now()}`);
            await uploadBytes(storageRef, photoFile);
            const photoUrl = await getDownloadURL(storageRef);

            // Update Firestore with photo URL
            await updateDoc(doc(this.firestore, 'users', user.id), {
                photoUrl: photoUrl,
                updatedAt: new Date()
            });

            // Update local state
            const updatedUser = { ...user, photoUrl };
            this.currentUserSignal.set(updatedUser);
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        } catch (error) {
            console.error('Error updating photo:', error);
            throw error;
        }
    }
}
